import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faChevronDown,
  faChevronUp,
  faChevronCircleRight,
  faStar,
  faPencilAlt,
  faWrench,
  faDollyFlatbed,
  faClipboardList,
  faLink,
  faCircle,
  faNewspaper,
  faLifeRing,
  faExternalLinkAlt,
  faThLarge,
  faChartBar,
  faCheckCircle,
  faExclamationCircle,
  faThumbtack,
  faPlusCircle,
  faTrash,
  faArrowDown,
  faArrowUp,
  faFilter,
  faLayerGroup,
  faSort,
  faTimes,
  faCog,
  faFileExport,
  faFileImport,
  faUndo,
  faRedo,
  faGripLinesVertical,
  faGripVertical,
  faHandsHelping,
  faQuestionCircle,
  faUpload,
  faImage,
  faEdit,
  faSave,
  faSpinner,
  faExclamationTriangle,
  faEye,
  faDownload,
  faMinusCircle,
  faPenAlt,
  faFileDownload,
  faClipboardCheck,
  faSquare,
  faClock,
  faPlus,
  faMagic
} from '@fortawesome/free-solid-svg-icons';
import { MatDialogModule } from '@angular/material/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  AppComponent,
  ActionLinksPanelComponent,
  DataEntryExampleComponent,
  FooterComponent,
  HeaderComponent,
  LoginComponent,
  MainMenuComponent,
  SidebarComponent,
  ReallyClickModalComponent,
  SearchTestsComponent,
  ValidationResultPanelComponent,
  ExceptionExampleComponent,
  ErrorLogsComponent,
  ErrorLogComponent,
  ExamplesComponent,
  ExampleComponent,
  NotificationExampleComponent,
  SearchGridLinkDestinationExampleComponent,
  SearchGridFormatExampleComponent,
  UnsavedChangesDialogComponent,
  CreateAccountComponent,
  ExampleFormlyComponent,
  DevHelpComponent,
  ApplicationSettingsComponent,
  ApplicationSettingComponent,
  CheckpointsComponent,
  CheckpointComponent,
  RolesComponent,
  RoleComponent,
  UserComponent,
  SpkDateComponent,
  SpkCurrencyComponent,
  SpkTextComponent,
  SpkSelectComponent,
  SpkCheckboxComponent,
  SpkTextareaComponent,
  SpkPasswordComponent,
  SpkNumberComponent,
  SpkTabComponent,
  SpkTabsComponent,
  SpkTabPaneComponent,
  SpkTabPanesComponent,
  SpkCardComponent,
  UsersComponent,
  AddressComponent,
  SpkSearchGridComponent,
  SpkPagerComponent,
  SpkValueTextCheckboxListComponent,
  SpkQuantityComponent,
  DashboardComponent,
  UserWidgetSelectionComponent,
  WidgetComponent,
  CountWidgetComponent,
  AdminTypeLinksComponent,
  CountriesComponent,
  CountryComponent,
  SpkLookupComponent,
  SpkModalLookupComponent,
  SpkControlsExampleComponent,
  QueuesComponent,
  SpkPercentComponent,
  ProjectReadinessStageComponent,
  NewProjectComponent,
  ProjectSummaryComponent,
  ProjectSummaryReadinessComponent,
  ProjectThumbnailComponent,
  ProjectListComponent,
  LandingPageComponent,
  ProjectSummaryMaterialsComponent,
  ProjectMaterialsStageComponent,
  ProjectMaterialsSubstageComponent,
  FeatureReleasesComponent,
  FeatureReleaseDetailComponent
} from './components';
import { AccessedProjectsComponent } from './components/accessed-projects/accessed-projects.component';
import { UserSessionsComponent } from './components/user-sessions/user-sessions.component';
import { FeatureReleaseService } from './services/feature-release.service';
import {
  ActionLinksPanelService,
  BusyService,
  CanDeactivateGuard,
  EditProjectAccessGuard,
  AuthGuard,
  ClientSessionService,
  ExecuteCommandService,
  PageContainerService,
  ReallyClickService,
  ValidationResultsService,
  UnsavedChangesDialogService,
  NotificationService,
  SearchGridService,
  BrowserNavigateService,
  TabService,
  ClientAssetDataService,
  TimerService,
  AlertDialogService
} from './services';
import { Notifier, ToastrNotifier, DateDtoPipe, StringSpacerPipe } from './utilities';
import { GlobalExceptionHandler } from './utilities/exceptions/GlobalExceptionHandler';
import { ReallyClickDirective } from './directives';
import { AppRoutingModule } from './app-routing.module';
import { ApiService } from './data/ApiService.generated';
import { PercentPipe } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ProjectSummaryExecutionComponent } from './components/project-summary-execution/project-summary-execution.component';
import { SummaryGaugeComponent } from './components/project-summary/summary-gauge.component';
import { PsOutlineIconComponent } from './components/project-summary/ps-outline-icon.component';
import { ProjectExecutionStageComponent } from './components/project-execution-stage/project-execution-stage.component';
import { ProjectCxplannerSummmaryComponent } from './components/project-cxplanner-summary/project-cxplanner-summary.component';
import { ProjectCxplannerStageComponent } from './components/project-cxplanner-stage/project-cxplanner-stage.component';
import { ProjectExecutionScurveComponent } from './components/project-execution-scurve/project-execution-scurve.component';
import { ProjectAskDataComponent } from './components/project-ask-data/project-ask-data.component';
import { AskDataFabComponent } from './components/ask-data-fab/ask-data-fab.component';
import { SCurveService } from './services/scurve.service';
import { ProjectReadniessFormComponent } from './components/project-readniess-form/project-readniess-form.component';
import { KeyValuePipe } from './utilities/pipes/keyvalue.pipe';
import { ReadinessAdminFormComponent } from './components/readiness-admin-form/readiness-admin-form.component';
import { ReadinessFormColumnSettingsComponent } from './components/readiness-form-column-settings/readiness-form-column-settings.component';
import { ReadinessTrafficLightsSettingsComponent } from './components/readiness-traffic-lights-settings/readiness-traffic-lights-settings.component';
import { SignalRService } from './services/signalr.service';
import { ReadinessGuideComponent } from './components/readiness-guide/readiness-guide.component';
import { SupportTicketComponent } from './components/support-ticket/support-ticket.component';
import { SupportTicketFormComponent } from './components/support-ticket/support-ticket-form.component';

export function minlengthValidationMessage(err, field) {
  return `Should have atleast ${field.templateOptions && field.templateOptions.minLength} characters`;
}

export function maxlengthValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions && field.templateOptions.maxLength} characters`;
}

export function minValidationMessage(err, field) {
  return `This value should be more than ${field.templateOptions && field.templateOptions.min}`;
}

export function maxValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions && field.templateOptions.max}`;
}

@NgModule({
  declarations: [
    AccessedProjectsComponent,
    ActionLinksPanelComponent,
    AddressComponent,
    AdminTypeLinksComponent,
    AppComponent,
    ApplicationSettingComponent,
    ApplicationSettingsComponent,
    CheckpointComponent,
    CheckpointsComponent,
    CountWidgetComponent,
    CountriesComponent,
    CountryComponent,
    CreateAccountComponent,
    DashboardComponent,
    DataEntryExampleComponent,
    DateDtoPipe,
    DevHelpComponent,
    ErrorLogComponent,
    ErrorLogsComponent,
    ExampleComponent,
    ExampleFormlyComponent,
    ExamplesComponent,
    ExceptionExampleComponent,
    FeatureReleaseDetailComponent,
    FeatureReleasesComponent,
    FooterComponent,
    HeaderComponent,
    KeyValuePipe,
    LandingPageComponent,
    LoginComponent,
    MainMenuComponent,
    NewProjectComponent,
    NotificationExampleComponent,
    ProjectCxplannerStageComponent,
    ProjectCxplannerSummmaryComponent,
    ProjectExecutionScurveComponent,
    ProjectAskDataComponent,
    AskDataFabComponent,
    ProjectExecutionStageComponent,
    ProjectListComponent,
    ProjectMaterialsStageComponent,
    ProjectMaterialsSubstageComponent,
    ProjectReadinessStageComponent,
    ProjectSummaryComponent,
    ProjectSummaryExecutionComponent,
    ProjectSummaryMaterialsComponent,
    ProjectSummaryReadinessComponent,
    SummaryGaugeComponent,
    PsOutlineIconComponent,
    ProjectThumbnailComponent,
    QueuesComponent,
    ReadinessAdminFormComponent,
    ReadinessFormColumnSettingsComponent,
    ReadinessGuideComponent,
    ReadinessTrafficLightsSettingsComponent,
    ReallyClickDirective,
    ReallyClickModalComponent,
    RoleComponent,
    RolesComponent,
    SearchGridFormatExampleComponent,
    SearchGridLinkDestinationExampleComponent,
    SearchTestsComponent,
    SidebarComponent,
    SpkCardComponent,
    SpkCheckboxComponent,
    SpkControlsExampleComponent,
    SpkCurrencyComponent,
    SpkDateComponent,
    SpkLookupComponent,
    SpkModalLookupComponent,
    SpkNumberComponent,
    SpkPagerComponent,
    SpkPasswordComponent,
    SpkPercentComponent,
    SpkQuantityComponent,
    SpkSearchGridComponent,
    SpkSelectComponent,
    SpkTabComponent,
    SpkTabPaneComponent,
    SpkTabPanesComponent,
    SpkTabsComponent,
    SpkTextComponent,
    SpkTextareaComponent,
    SpkValueTextCheckboxListComponent,
    StringSpacerPipe,
    SupportTicketComponent,
    SupportTicketFormComponent,
    UnsavedChangesDialogComponent,
    UserComponent,
    UserSessionsComponent,
    UserWidgetSelectionComponent,
    UsersComponent,
    ValidationResultPanelComponent,
    WidgetComponent,
  ],
  imports: [
    FontAwesomeModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    HttpClientXsrfModule,
    FormsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    DragDropModule,
    MatDialogModule,
    ProjectReadniessFormComponent,
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'required', message: 'This field is required' },
        { name: 'minlength', message: minlengthValidationMessage },
        { name: 'maxlength', message: maxlengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage },
      ],
    }),
    FormlyBootstrapModule,
    NgxChartsModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
  ],
  providers: [
    ActionLinksPanelService,
    BrowserNavigateService,
    BusyService,
    CanDeactivateGuard,
    EditProjectAccessGuard,
    AuthGuard,
    ClientSessionService,
    ExecuteCommandService,
    PageContainerService,
    ReallyClickService,
    ValidationResultsService,
    ApiService,
    UnsavedChangesDialogService,
    AlertDialogService,
    NotificationService,
    SearchGridService,
    TabService,
    DateDtoPipe,
    PercentPipe,
    FeatureReleaseService,
    { provide: ErrorHandler, useClass: GlobalExceptionHandler },
    { provide: Notifier, useClass: ToastrNotifier },
    ClientAssetDataService,
    SCurveService,
    SCurveService,
    SignalRService,
    TimerService

  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(
      faChevronRight,
      faChevronLeft,
      faChevronDown,
      faChevronUp,
      faChevronCircleRight,
      faStar,
      faPencilAlt,
      faWrench,
      faDollyFlatbed,
      faClipboardList,
      faLink,
      faCircle,
      faNewspaper,
      faLifeRing,
      faExternalLinkAlt,
      faThLarge,
      faChartBar,
      faCheckCircle,
      faExclamationCircle,
      faThumbtack,
      faPlusCircle,
      faTrash,
      faArrowDown,
      faArrowUp,
      faFilter,
      faLayerGroup,
      faSort,
      faTimes,
      faCog,
      faFileExport,
      faFileImport,
      faUndo,
      faRedo,
      faGripLinesVertical,
      faGripVertical,
      faHandsHelping,
      faQuestionCircle,
      faUpload,
      faImage,
      faEdit,
      faSave,
      faSpinner,
      faExclamationTriangle,
      faEye,
      faDownload,
      faMinusCircle,
      faPenAlt,
      faFileDownload,
      faClipboardCheck,
      faSquare,
      faClock,
      faPlus,
      faMagic
    );
  }
}
